import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from './Theme';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIIcon from 'UIComponents/icon/UIIcon';
import { THUNDERDOME } from 'HubStyleTokens/colors';
import { GRID_BREAKPOINT_SMALL } from 'HubStyleTokens/sizes';
import Small from 'UIComponents/elements/Small';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import AvatarSkeleton from './AvatarSkeleton';
import ProductTag from './ProductTag';
import { useMediaQuery } from '../utils/hooks';
import { Skeleton } from './Skeleton';
import AvatarViewer from '../features/avatar/components/AvatarViewer';
const LinkWrapper = styled(UIRouterLink).withConfig({
  displayName: "ProfileCard__LinkWrapper",
  componentId: "sc-1gok152-0"
})(["height:fit-content;"]);
const CardWrapper = styled(UICardWrapper).withConfig({
  displayName: "ProfileCard__CardWrapper",
  componentId: "sc-1gok152-1"
})(["padding:24px;margin-bottom:0 !important;&:hover{box-shadow:0 1px 10px 3px rgb(45 62 80 / 12%);}@media (max-width:", "){flex-direction:row;align-items:center;padding:12px;margin-left:-12px !important;margin-right:-12px !important;width:calc(100% + 24px);height:auto;}"], props => props.theme.sizes.GRID_BREAKPOINT_SMALL);
const VisualLink = styled.span.withConfig({
  displayName: "ProfileCard__VisualLink",
  componentId: "sc-1gok152-2"
})(["color:", ";font-weight:600;width:fit-content;margin-top:auto;&:hover{text-decoration:underline;}"], props => props.theme.colors.CALYPSO_DARK);
const NameText = styled.p.withConfig({
  displayName: "ProfileCard__NameText",
  componentId: "sc-1gok152-3"
})(["font-size:20px;font-weight:500;margin-bottom:0;@media (max-width:", "){font-size:16px;}"], props => props.theme.sizes.GRID_BREAKPOINT_SMALL);
const JobText = styled.p.withConfig({
  displayName: "ProfileCard__JobText",
  componentId: "sc-1gok152-4"
})(["margin-bottom:0;@media (max-width:", "){font-size:12px;line-height:18px;}"], props => props.theme.sizes.GRID_BREAKPOINT_SMALL);
const GoalText = styled.p.withConfig({
  displayName: "ProfileCard__GoalText",
  componentId: "sc-1gok152-5"
})(["display:inline;position:relative;color:", ";font-weight:500;font-size:16px;top:-3px;@media (max-width:", "){font-size:12px;line-height:18px;top:-1.5px;}"], props => props.theme.colors.THUNDERDOME, props => props.theme.sizes.GRID_BREAKPOINT_SMALL);

const GoalTag = (_ref) => {
  let {
    goal,
    isSmall
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["goal", "isSmall"]);

  return /*#__PURE__*/_jsxs("div", Object.assign({}, props, {
    "data-test-id": "goal-tag",
    children: [/*#__PURE__*/_jsx(UIIcon, {
      className: "m-right-1",
      name: "goal",
      size: isSmall ? 18 : 24,
      color: THUNDERDOME
    }), /*#__PURE__*/_jsx(GoalText, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `connectDotCom.goals.${goal}`
      })
    })]
  }));
};

const ProfileCard = ({
  name,
  jobTitle,
  goal,
  products,
  username,
  avatarHash,
  avatarLastUpdated
}) => {
  const screenLarge = useMediaQuery(`(min-width: ${GRID_BREAKPOINT_SMALL})`);
  return /*#__PURE__*/_jsx(LinkWrapper, {
    "data-test-id": "profile-card",
    use: "unstyled",
    to: `/member/${username}`,
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      children: [/*#__PURE__*/_jsx("div", {
        className: screenLarge ? 'm-bottom-4' : 'm-right-4',
        children: /*#__PURE__*/_jsx(AvatarViewer, {
          avatarHash: avatarHash,
          lastUpdated: avatarLastUpdated,
          size: screenLarge ? 'xl' : 'md'
        })
      }), /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsxs("div", {
          className: screenLarge ? 'm-bottom-4' : '',
          children: [/*#__PURE__*/_jsx(NameText, {
            "data-test-id": "user-display-name",
            children: name
          }), /*#__PURE__*/_jsx(JobText, {
            children: jobTitle
          })]
        }), goal && /*#__PURE__*/_jsx(GoalTag, {
          className: screenLarge ? 'm-bottom-4' : '',
          isSmall: !screenLarge,
          goal: goal
        })]
      }), screenLarge && /*#__PURE__*/_jsxs(_Fragment, {
        children: [!!products.length && /*#__PURE__*/_jsxs("div", {
          className: "m-bottom-4",
          children: [/*#__PURE__*/_jsx(Small, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "connectDotCom.productsUsed"
            })
          }), /*#__PURE__*/_jsx("div", {
            className: "m-top-1",
            children: products.map(product => /*#__PURE__*/_jsx(ProductTag, {
              product: product
            }, product))
          })]
        }), /*#__PURE__*/_jsx(VisualLink, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "connectDotCom.viewProfile"
          })
        })]
      })]
    })
  });
};

export const ProfileCardSkeleton = () => {
  const screenLarge = useMediaQuery(`(min-width: ${GRID_BREAKPOINT_SMALL})`);
  return /*#__PURE__*/_jsxs(CardWrapper, {
    height: screenLarge ? 480 : 90,
    children: [/*#__PURE__*/_jsx(AvatarSkeleton, {
      size: screenLarge ? 100 : 40,
      className: screenLarge ? 'm-bottom-4' : 'm-right-4'
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        flexGrow: 1,
        height: 64
      },
      children: [/*#__PURE__*/_jsx(Skeleton, {
        width: "70%",
        height: 16,
        className: "m-bottom-2"
      }), /*#__PURE__*/_jsx(Skeleton, {
        width: "90%",
        height: 12,
        className: screenLarge ? 'm-bottom-8' : 'm-bottom-2'
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-bottom-8",
        children: [/*#__PURE__*/_jsx(Skeleton, {
          width: 12,
          height: 12,
          radius: "50%",
          className: "m-right-2",
          inline: true
        }), /*#__PURE__*/_jsx(Skeleton, {
          width: "75%",
          height: 12,
          inline: true
        })]
      })]
    }), screenLarge && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Skeleton, {
        width: "40%",
        height: 12,
        className: "m-bottom-2"
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-bottom-2",
        children: [/*#__PURE__*/_jsx(Skeleton, {
          width: 20,
          height: 20,
          className: "m-right-2",
          inline: true
        }), /*#__PURE__*/_jsx(Skeleton, {
          width: "60%",
          height: 20,
          inline: true
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-bottom-2",
        children: [/*#__PURE__*/_jsx(Skeleton, {
          width: 20,
          height: 20,
          className: "m-right-2",
          inline: true
        }), /*#__PURE__*/_jsx(Skeleton, {
          width: "45%",
          height: 20,
          inline: true
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-bottom-8",
        children: [/*#__PURE__*/_jsx(Skeleton, {
          width: 20,
          height: 20,
          className: "m-right-2",
          inline: true
        }), /*#__PURE__*/_jsx(Skeleton, {
          width: "45%",
          height: 20,
          inline: true
        })]
      }), /*#__PURE__*/_jsx(Skeleton, {
        width: "30%",
        height: 16
      })]
    })]
  });
};
export default ProfileCard;